// src/state/homeDataStore.js
// Filepath: // src/state/homeDataStore.js

import { create } from "zustand";
import { getUserProfileByToken, getMyHoldings, getFreeContent, getMyActivity } from "../lib/kbeanApi";
import { buildRails } from "../lib/contentNormalizer";
import { addActivityRail, normalizeActivityItems } from "../lib/contentActivityRail";

// How long we consider data "fresh" (skip fetching unless force=true)
const TTL_MS = 5 * 60 * 1000; // 5 minutes

const epInit = { data: null, status: "idle", error: null, updatedAt: 0 };

/**
 * Home data store:
 * - Fetches profile, holdings, free content, AND activity
 * - selectRails(): uses your existing buildRails, then appends Live Sales
 * - selectActivityItems(): returns normalized items for the Activity grid scene
 */
export const useHomeDataStore = create((set, get) => {
  const run = async (key, fn, signal) => {
    set((s) => ({ [key]: { ...s[key], status: "loading", error: null } }));
    try {
      const data = await fn();
      if (signal?.aborted) throw new Error("aborted");
      set(() => ({
        [key]: { data, status: "success", error: null, updatedAt: Date.now() },
      }));
      return data;
    } catch (err) {
      set((s) => ({
        [key]: { ...s[key], status: "error", error: err, updatedAt: Date.now() },
      }));
      throw err;
    }
  };

  const needsRefresh = (now = Date.now()) => {
    const { profile, holdings, free, activity } = get();
    const fresh =
      profile.data &&
      holdings.data &&
      free.data &&
      activity.data &&
      now - profile.updatedAt < TTL_MS &&
      now - holdings.updatedAt < TTL_MS &&
      now - free.updatedAt < TTL_MS &&
      now - activity.updatedAt < TTL_MS;
    return !fresh;
  };

  const refresh = async ({ force = false, signal } = {}) => {
    if (!force && !needsRefresh()) return get();

    set({ isRefreshing: true });

    const tasks = [
      run("profile", getUserProfileByToken, signal),
      run("holdings", getMyHoldings, signal),
      run("free", getFreeContent, signal),
      run("activity", getMyActivity, signal),
    ];

    await Promise.allSettled(tasks);

    set({ isRefreshing: false, lastRefreshAt: Date.now() });
    return get();
  };

  return {
    TTL_MS,
    profile: { ...epInit },
    holdings: { ...epInit },
    free: { ...epInit },
    activity: { ...epInit }, // NEW
    isRefreshing: false,
    lastRefreshAt: 0,

    refresh,
    needsRefresh,

    /**
     * Build normalized home rails from current store data.
     * We preserve your existing rail builder and then append Live Sales.
     */
    selectRails() {
      const freeCollections = get().free.data;
      const myHoldings = get().holdings.data;
      let rails = buildRails({ freeCollections, myHoldings });

      const activityData = get().activity.data;
      rails = addActivityRail(rails, activityData);
      return rails;
    },

    /**
     * Return normalized activity items for the Activity Grid scene.
     */
    selectActivityItems() {
      const activityData = get().activity.data;
      return normalizeActivityItems(activityData);
    },

    clear: () =>
      set({
        profile: { ...epInit },
        holdings: { ...epInit },
        free: { ...epInit },
        activity: { ...epInit },
        isRefreshing: false,
        lastRefreshAt: 0,
      }),
  };
});
